import { useEffect, useRef, useState } from "react";

export const IDLE = { isPaused: true, position: 0, duration: 0 };

// Plain <audio> engine: uploaded files and Spotify 30s previews.
export function useAudioEngine(onEnded, volume, muted) {
  const el = useRef(null);
  const [pb, setPb] = useState(IDLE);

  useEffect(() => {
    const a = new Audio();
    a.preload = "none";
    el.current = a;
    const sync = () => setPb({ isPaused: a.paused, position: a.currentTime * 1000, duration: Number.isFinite(a.duration) ? a.duration * 1000 : 0 });
    const end = () => onEnded.current();
    const events = ["timeupdate", "play", "pause", "loadedmetadata", "durationchange", "seeked"];
    events.forEach((e) => a.addEventListener(e, sync));
    a.addEventListener("ended", end);
    return () => {
      a.pause();
      events.forEach((e) => a.removeEventListener(e, sync));
      a.removeEventListener("ended", end);
    };
  }, [onEnded]);

  useEffect(() => {
    if (!el.current) return;
    el.current.volume = volume;
    el.current.muted = muted;
  }, [volume, muted]);

  return {
    pb,
    load: (src) => { const a = el.current; a.src = src; a.play().catch(() => {}); },
    toggle: () => { const a = el.current; if (a.paused) a.play().catch(() => {}); else a.pause(); },
    pause: () => el.current?.pause(),
    seek: (ms) => { if (el.current) el.current.currentTime = ms / 1000; },
  };
}

let ytPromise;
const loadYT = () => {
  if (!ytPromise) {
    ytPromise = new Promise((resolve) => {
      if (window.YT?.Player) { resolve(window.YT); return; }
      const prev = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = () => { if (prev) prev(); resolve(window.YT); };
      const s = document.createElement("script");
      s.src = "https://www.youtube.com/iframe_api";
      s.async = true;
      document.body.appendChild(s);
    });
  }
  return ytPromise;
};

const applyVolume = (p, volume, muted) => {
  p.setVolume(Math.round(volume * 100));
  if (muted) p.mute(); else p.unMute();
};

// Hidden YouTube player engine: YouTube / YouTube Music tracks and full versions of Spotify songs.
export function useYouTubeEngine({ onEnded, onError, enabled, active, volume, muted }) {
  const host = useRef(null);
  const player = useRef(null);
  const ready = useRef(false);
  const pending = useRef(null);
  const vol = useRef({ volume, muted });
  vol.current = { volume, muted };
  const [pb, setPb] = useState(IDLE);

  useEffect(() => {
    if (!enabled || player.current) return undefined;
    let alive = true;
    loadYT().then((YT) => {
      if (!alive || !host.current || player.current) return;
      const div = document.createElement("div");
      host.current.appendChild(div);
      player.current = new YT.Player(div, {
        width: 320, height: 180,
        playerVars: { controls: 0, playsinline: 1, disablekb: 1, rel: 0, origin: window.location.origin },
        events: {
          onReady: () => {
            ready.current = true;
            applyVolume(player.current, vol.current.volume, vol.current.muted);
            if (pending.current) { player.current.loadVideoById(pending.current); pending.current = null; }
          },
          onStateChange: (e) => { if (e.data === 0) onEnded.current(); },
          onError: () => onError.current(),
        },
      });
    });
    return () => { alive = false; };
  }, [enabled, onEnded, onError]);

  useEffect(() => {
    if (ready.current) applyVolume(player.current, volume, muted);
  }, [volume, muted]);

  useEffect(() => {
    if (!active) return undefined;
    const t = setInterval(() => {
      const p = player.current;
      if (!ready.current || !p?.getPlayerState) return;
      setPb({ isPaused: p.getPlayerState() !== 1, position: (p.getCurrentTime() || 0) * 1000, duration: (p.getDuration() || 0) * 1000 });
    }, 500);
    return () => clearInterval(t);
  }, [active]);

  return {
    host,
    pb,
    load: (id) => {
      setPb(IDLE);
      if (ready.current) player.current.loadVideoById(id);
      else pending.current = id;
    },
    toggle: () => {
      const p = player.current;
      if (!ready.current) return;
      if (p.getPlayerState() === 1) p.pauseVideo(); else p.playVideo();
    },
    pause: () => { if (ready.current) player.current.pauseVideo(); },
    seek: (ms) => {
      if (!ready.current) return;
      player.current.seekTo(ms / 1000, true);
      setPb((p) => ({ ...p, position: ms }));
    },
  };
}
